import React, { useState, useEffect } from 'react';
import { 
  Flame, Sword, Moon, Sun, Shield, Eye, Zap, 
  CheckCircle2, Circle, X, RefreshCcw, BookOpen, 
  Quote, Compass, Anchor, User, Activity
} from 'lucide-react';

const ConsciousWarriorTracker = () => {
  // --- CONFIGURACIÓN Y ESTADO ---
  const STORAGE_KEY = 'conscious_warrior_data_v3';
  
  const initialChecklist = {
    morningMantra: false,
    morningWriting: false,
    dayDecision: false,
    dayDesire: false,
    afternoonBody: false,
    nightMantra: false,
  };

  const loadState = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error("Error cargando datos", e);
    }
    return {
      currentDay: 1,
      intention: '',
      checklist: initialChecklist
    };
  };

  const [data, setData] = useState(loadState);
  const [showEmergency, setShowEmergency] = useState(false);
  const [showWisdom, setShowWisdom] = useState(false);
  
  // Estado para las pestañas dentro del modal de sabiduría
  const [wisdomTab, setWisdomTab] = useState('diagnostico'); 

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  // --- LÓGICA DE FASES ---
  const phases = [
    { range: [1, 10], title: "Fase 1: Cuerpo y Presencia", focus: "Habitar sin explicar. Silencio." },
    { range: [11, 20], title: "Fase 2: Decisión y Corte", focus: "Elegir. Decir No. Retirarse." },
    { range: [21, 30], title: "Fase 3: Deseo Expresado", focus: "Separar deseo de aprobación." },
    { range: [31, 40], title: "Fase 4: Dirección Pura", focus: "Convertirse en punto estable." }
  ];

  const currentPhase = phases.find(p => data.currentDay >= p.range[0] && data.currentDay <= p.range[1]) || phases[3];
  const progressPercent = (data.currentDay / 40) * 100;

  // --- HANDLERS ---
  const toggleTask = (task) => {
    setData(prev => ({
      ...prev,
      checklist: { ...prev.checklist, [task]: !prev.checklist[task] }
    }));
  };

  const handleIntentionChange = (e) => setData(prev => ({ ...prev, intention: e.target.value }));

  const advanceDay = () => {
    if (data.currentDay < 40) {
      if (window.confirm("¿Confirmás que cerrás este día? Se reiniciarán las tareas para mañana.")) {
        setData(prev => ({
          ...prev,
          currentDay: prev.currentDay + 1,
          checklist: initialChecklist,
          intention: ''
        }));
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const resetAll = () => {
    if (window.confirm("¿⚠️ Estás seguro? Esto borrará todo tu progreso de los 40 días.")) {
      setData({ currentDay: 1, intention: '', checklist: initialChecklist });
      setShowEmergency(false);
      setShowWisdom(false);
    }
  };

  // --- COMPONENTES UI INTERNOS ---
  const SectionTitle = ({ icon: Icon, title, color = "text-white" }) => (
    <div className={`flex items-center gap-2 mb-4 ${color} uppercase tracking-widest font-bold text-sm`}>
      <Icon size={18} />
      <h3>{title}</h3>
    </div>
  );

  const CheckItem = ({ label, isChecked, onClick, subtext }) => (
    <div 
      onClick={onClick}
      className={`group cursor-pointer p-4 rounded-xl border transition-all duration-300 flex items-start gap-4 select-none
      ${isChecked 
        ? 'bg-purple-900/20 border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.1)]' 
        : 'bg-gray-900/40 border-gray-800 hover:border-gray-600 hover:bg-gray-900/60'}`}
    >
      <div className={`mt-1 transition-colors duration-300 ${isChecked ? 'text-purple-400' : 'text-gray-600 group-hover:text-gray-400'}`}>
        {isChecked ? <CheckCircle2 size={24} /> : <Circle size={24} />}
      </div>
      <div>
        <p className={`font-medium transition-colors ${isChecked ? 'text-white' : 'text-gray-300'}`}>{label}</p>
        {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
      </div>
    </div>
  );

  const WisdomTabButton = ({ id, label, icon: Icon }) => (
    <button
      onClick={() => setWisdomTab(id)}
      className={`flex items-center gap-2 px-4 py-3 text-sm font-bold uppercase tracking-wider transition-all border-b-2
        ${wisdomTab === id 
          ? 'border-purple-500 text-white bg-white/5' 
          : 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-white/5'}`}
    >
      <Icon size={16} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-black text-gray-200 p-4 md:p-8 font-sans selection:bg-purple-500/30 flex flex-col items-center">
      
      <div className="w-full max-w-4xl space-y-12">
        
        {/* HEADER */}
        <header className="text-center space-y-4 pt-8 animate-fade-in-down">
          <div className="inline-flex items-center justify-center p-3 rounded-full bg-gradient-to-br from-orange-500/20 to-purple-900/20 border border-orange-500/30 mb-4 shadow-[0_0_20px_rgba(249,115,22,0.15)]">
            <Flame className="text-orange-500 w-8 h-8" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-b from-gray-100 to-gray-500 tracking-tighter uppercase">
            El Guerrero Consciente
          </h1>
          <p className="text-gray-500 max-w-lg mx-auto text-sm md:text-base">
            No persigas conexión. Conviértete en eje.
          </p>
        </header>

        {/* PROGRESS BAR */}
        <div className="bg-gray-950 rounded-2xl p-6 border border-gray-800 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-900/5 rounded-full blur-3xl -z-0 pointer-events-none"></div>
          <div className="relative z-10">
            <div className="flex justify-between items-end mb-3">
              <div>
                <span className="text-xs font-bold text-purple-400 uppercase tracking-widest">Día {data.currentDay} de 40</span>
                <h2 className="text-xl font-bold text-white mt-1">{currentPhase.title}</h2>
              </div>
              <div className="text-right hidden sm:block">
                <span className="text-xs text-gray-500 uppercase">Foco Actual</span>
                <p className="text-sm font-medium text-orange-400">{currentPhase.focus}</p>
              </div>
            </div>
            <div className="h-3 w-full bg-gray-900 rounded-full overflow-hidden border border-gray-800">
              <div 
                className="h-full bg-gradient-to-r from-purple-700 to-orange-500 transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(168,85,247,0.4)]"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="mt-6 flex justify-end">
              <button 
                onClick={advanceDay}
                disabled={data.currentDay >= 40}
                className={`text-xs flex items-center gap-2 px-4 py-2 rounded-lg transition-all font-bold tracking-wide
                  ${data.currentDay >= 40 
                    ? 'bg-green-900/20 text-green-500 cursor-default' 
                    : 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700'}`}
              >
                {data.currentDay >= 40 ? "COMPLETADO" : "CERRAR DÍA & AVANZAR"} <Sword size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* --- GRID DE RUTINA --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* MAÑANA */}
          <div className="space-y-4">
            <SectionTitle icon={Sun} title="Mañana (6:00 - 8:00)" color="text-orange-400" />
            <CheckItem 
              label="Mantra: Om Hram Shivaya Namah" 
              subtext="7 mins. Visualizá densidad, no personas."
              isChecked={data.checklist.morningMantra}
              onClick={() => toggleTask('morningMantra')}
            />
            <div className={`p-4 rounded-xl border bg-gray-900/30 transition-all duration-300 group
              ${data.intention ? 'border-orange-500/40 bg-orange-900/10' : 'border-gray-800 focus-within:border-orange-500/50'}`}>
              <div className="flex items-center gap-2 mb-2 text-gray-400 text-sm">
                <Sword size={14} className={data.intention ? "text-orange-400" : ""} />
                <span className={data.intention ? "text-orange-200" : ""}>Dirección del día</span>
              </div>
              <input 
                type="text" 
                value={data.intention}
                onChange={handleIntentionChange}
                placeholder="Hoy mi energía va hacia..."
                className="w-full bg-transparent border-b border-gray-700 focus:border-orange-500 outline-none text-white py-2 placeholder:text-gray-600 transition-colors"
              />
            </div>
          </div>

          {/* DÍA */}
          <div className="space-y-4">
            <SectionTitle icon={Eye} title="Durante el Día" color="text-blue-400" />
            <CheckItem 
              label="Decisión Unilateral" 
              subtext="Tomar una decisión sin buscar consenso."
              isChecked={data.checklist.dayDecision}
              onClick={() => toggleTask('dayDecision')}
            />
             <CheckItem 
              label="Gestión del Silencio" 
              subtext="Cortar charla emocional innecesaria."
              isChecked={data.checklist.dayDesire}
              onClick={() => toggleTask('dayDesire')}
            />
          </div>

          {/* TARDE */}
          <div className="space-y-4">
            <SectionTitle icon={Shield} title="Tarde (16:00 - 18:00)" color="text-green-400" />
            <CheckItem 
              label="Cuerpo y Tierra" 
              subtext="Entrenamiento, Armonio o Caminata sola."
              isChecked={data.checklist.afternoonBody}
              onClick={() => toggleTask('afternoonBody')}
            />
          </div>

          {/* NOCHE */}
          <div className="space-y-4">
            <SectionTitle icon={Moon} title="Noche (Cierre)" color="text-purple-400" />
            <CheckItem 
              label="Mantra: Om Lam Shantih" 
              subtext="Mano en vientre. Cortar proyección."
              isChecked={data.checklist.nightMantra}
              onClick={() => toggleTask('nightMantra')}
            />
             <div className="p-4 rounded-xl bg-black border border-gray-800 text-center shadow-lg">
                <p className="text-gray-400 text-sm italic font-serif tracking-wide">"Mi energía vuelve a mí."</p>
             </div>
          </div>
        </div>

        {/* --- FOOTER DE ACCIONES --- */}
        <div className="flex flex-col md:flex-row justify-center items-center gap-6 pt-8 pb-16">
          
          <button 
            onClick={() => setShowWisdom(true)}
            className="group flex items-center gap-3 px-8 py-4 rounded-full bg-gray-900 border border-gray-700 text-gray-200 hover:text-white hover:border-purple-500 hover:bg-gray-800 transition-all shadow-lg hover:shadow-purple-900/20"
          >
            <BookOpen size={20} className="text-purple-500 group-hover:text-purple-400" />
            <span className="font-bold text-sm tracking-wide">BIBLIOTECA DEL GUERRERO</span>
          </button>

          <button 
            onClick={() => setShowEmergency(true)}
            className="group flex items-center gap-3 px-8 py-4 rounded-full bg-red-950/20 border border-red-900/60 text-red-500 hover:bg-red-900/40 hover:border-red-500 hover:text-red-400 transition-all shadow-lg"
          >
            <Zap size={20} />
            <span className="font-bold text-sm tracking-wide">PROTOCOLO S.O.S</span>
          </button>

          <button 
            onClick={resetAll}
            className="text-gray-800 hover:text-gray-500 text-xs flex items-center gap-1 transition-colors mt-4 md:mt-0"
          >
            <RefreshCcw size={10} /> Reset
          </button>
        </div>

      </div>

      {/* --- MODAL 1: PROTOCOLO DE EMERGENCIA (S.O.S) --- */}
      {showEmergency && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-md animate-in fade-in duration-300">
          <div className="max-w-lg w-full bg-gray-950 border border-red-900/50 rounded-2xl p-8 relative shadow-2xl shadow-red-900/20 flex flex-col items-center">
            <button 
              onClick={() => setShowEmergency(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
            <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mb-6 animate-pulse">
              <Shield className="text-red-600 w-10 h-10" />
            </div>
            <h3 className="text-3xl font-black text-white uppercase tracking-tighter mb-6 text-center">Protocolo de Retorno</h3>
            <div className="w-full space-y-4 text-left bg-gray-900/80 p-6 rounded-xl border border-red-900/30 mb-8">
              <p className="text-gray-300 font-medium flex gap-3"><span className="text-red-500 font-bold">1.</span> Retirá la energía del escenario.</p>
              <p className="text-gray-300 font-medium flex gap-3"><span className="text-red-500 font-bold">2.</span> Bajá la respiración al vientre bajo.</p>
              <p className="text-gray-300 font-medium flex gap-3"><span className="text-red-500 font-bold">3.</span> Repetí internamente:</p>
              <blockquote className="border-l-4 border-red-600 pl-4 py-2 mt-4 text-xl font-serif italic text-white bg-black/40 rounded-r-lg">
                "No persigo conexión. <br/>
                <span className="text-red-500 not-italic font-black uppercase text-lg">Me convierto en eje.</span>"
              </blockquote>
            </div>
            <button 
              onClick={() => setShowEmergency(false)}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-gray-200 to-white text-black font-black tracking-widest hover:scale-[1.02] transition-transform"
            >
              ENTENDIDO. VOLVER AL EJE.
            </button>
          </div>
        </div>
      )}

      {/* --- MODAL 2: BIBLIOTECA DEL GUERRERO (FULL CONTENT) --- */}
      {showWisdom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-6 bg-black/95 backdrop-blur-md animate-in fade-in duration-300">
          <div className="w-full max-w-5xl h-full md:max-h-[90vh] bg-gray-950 border border-gray-800 rounded-none md:rounded-2xl relative shadow-2xl flex flex-col">
            
            {/* Cabecera Modal */}
            <div className="bg-gray-950 border-b border-gray-800 p-4 md:p-6 flex justify-between items-center shrink-0">
              <div className="flex items-center gap-3">
                <BookOpen className="text-purple-500" />
                <h3 className="text-lg md:text-xl font-bold text-white uppercase tracking-wider">Bitácora de Sabiduría</h3>
              </div>
              <button 
                onClick={() => setShowWisdom(false)}
                className="text-gray-500 hover:text-white transition-colors p-2"
              >
                <X size={24} />
              </button>
            </div>

            {/* Navegación Tabs */}
            <div className="flex overflow-x-auto border-b border-gray-800 bg-black/50 shrink-0">
              <WisdomTabButton id="diagnostico" label="1. Diagnóstico" icon={User} />
              <WisdomTabButton id="astrologia" label="2. Diseño" icon={Moon} />
              <WisdomTabButton id="principios" label="3. Principios" icon={Compass} />
              <WisdomTabButton id="practica" label="4. Práctica" icon={Activity} />
              <WisdomTabButton id="verdades" label="5. Verdades" icon={Quote} />
            </div>

            {/* Contenido Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 md:p-10 bg-gray-900/30">
              
              {/* --- TAB 1: DIAGNÓSTICO --- */}
              {wisdomTab === 'diagnostico' && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="bg-orange-900/10 border-l-4 border-orange-500 p-6 rounded-r-xl">
                    <h4 className="text-orange-400 font-bold uppercase mb-2">El Patrón Raíz (Familia)</h4>
                    <p className="text-gray-300 leading-relaxed text-sm md:text-base">
                      Creciste viendo un masculino que no lidera y un femenino que empuja. Aprendiste inconscientemente que ser masculino es replegarse y observar. Desarrollaste una energía femenina refinada (escucha, ternura), pero el vector masculino (deseo, avance) quedó inhibido.
                    </p>
                    <p className="text-gray-300 mt-4 font-bold">Resultado: Atraés mujeres con energía masculina fuerte, pero con las femeninas no hay polaridad sexual.</p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
                      <h4 className="text-purple-400 font-bold uppercase mb-2 flex items-center gap-2">
                        <Activity size={16} /> Lo que pasó con Ella (Umepay)
                      </h4>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        "Estoy casi seguro de que ya no estoy enamorado, pero hay una inercia."
                        <br/><br/>
                        Eso no es amor. Es identidad no resuelta. Ella representó la versión de vínculo que tu sistema todavía no sabe sostener desde lo masculino.
                      </p>
                      <div className="bg-black/40 p-3 rounded-lg border border-purple-900/30">
                        <p className="text-gray-300 text-sm italic">
                          "Por eso duele verla con otro: no porque la quieras, sino porque el ego masculino siente: 'No fui elegido como hombre'."
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
                      <h4 className="text-red-400 font-bold uppercase mb-2 flex items-center gap-2">
                        <Eye size={16} /> El Espejo Invertido
                      </h4>
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        La chica que te desea y no te atrae es oro puro. Ella proyecta su deseo, vos la sentís invasiva y no hay atracción.
                      </p>
                      <div className="bg-black/40 p-3 rounded-lg border border-red-900/30">
                        <p className="text-gray-300 text-sm italic">
                          "Eso es exactamente lo que muchas mujeres sienten con vos, pero nunca te lo dicen. No es castigo, es feedback del campo."
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* --- TAB 2: ASTROLOGÍA --- */}
              {wisdomTab === 'astrologia' && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                   <div className="bg-indigo-900/10 border border-indigo-500/30 p-6 rounded-xl text-center">
                    <h4 className="text-indigo-300 font-bold uppercase tracking-widest text-lg mb-2">Tu Mapa Energético</h4>
                    <p className="text-gray-400">Escorpio ☀️ • Aries ⬆️ • Libra 🌙 • Proyector</p>
                  </div>

                  <div className="grid gap-4">
                    <div className="flex gap-4 p-5 bg-gray-900/50 rounded-xl border border-gray-800">
                      <Sun className="text-orange-500 shrink-0" />
                      <div>
                        <h5 className="font-bold text-white">Escorpio + Ascendente Aries</h5>
                        <p className="text-sm text-gray-400 mt-1">
                          Tenés fuego para iniciar (Aries) y potencia sexual profunda (Escorpio). Pero si no se expresa en acción clara, se vuelve intensidad contenida.
                          <br/><span className="text-orange-400 italic">"Hay algo... pero no pasa nada."</span>
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4 p-5 bg-gray-900/50 rounded-xl border border-gray-800">
                      <Moon className="text-blue-400 shrink-0" />
                      <div>
                        <h5 className="font-bold text-white">Luna en Libra</h5>
                        <p className="text-sm text-gray-400 mt-1">
                          Hipersensibilidad al otro. Tendencia a armonizar, esperar señales y no incomodar. Esto modera tu fuego y te hace priorizar el "buen clima" sobre el deseo.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4 p-5 bg-gray-900/50 rounded-xl border border-gray-800">
                      <Anchor className="text-green-400 shrink-0" />
                      <div>
                        <h5 className="font-bold text-white">Proyector (Diseño Humano)</h5>
                        <p className="text-sm text-gray-400 mt-1">
                          No estás para forzar, pero sí para reconocer cuándo guiar. El problema es querer vincularte como Generador emocional (empujando).
                          <br/><span className="text-green-400 font-bold">Tu arquetipo: El Guerrero Consciente (Silencioso).</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* --- TAB 3: PRINCIPIOS --- */}
              {wisdomTab === 'principios' && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <h4 className="text-white font-bold text-xl uppercase text-center mb-6">Qué es realmente potenciar tu masculino</h4>
                  
                  <div className="grid gap-6">
                    {/* Principio 1 */}
                    <div className="relative pl-8 border-l-2 border-gray-700 hover:border-orange-500 transition-colors">
                      <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-gray-900 border-2 border-orange-500"></div>
                      <h5 className="text-orange-400 font-bold text-lg mb-2">1. Dirección Interna Clara</h5>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Antes de cualquier vínculo, preguntate: <strong>"¿Qué quiero yo, aunque el otro no lo quiera?"</strong>. Y sostené eso sin explicarlo. Retirarte a tiempo es profundamente masculino.
                      </p>
                    </div>

                    {/* Principio 2 */}
                    <div className="relative pl-8 border-l-2 border-gray-700 hover:border-purple-500 transition-colors">
                      <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-gray-900 border-2 border-purple-500"></div>
                      <h5 className="text-purple-400 font-bold text-lg mb-2">2. Deseo Expresado (No sugerido)</h5>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Vos conectás y contenés, pero no avanzás. Una mujer femenina necesita sentir: 
                        <span className="italic block mt-1 text-white">"Este hombre me desea y no tiene miedo de perderme por expresarlo."</span>
                        Eso no es invadir. Es polarizar.
                      </p>
                    </div>

                    {/* Principio 3 */}
                    <div className="relative pl-8 border-l-2 border-gray-700 hover:border-red-500 transition-colors">
                      <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-gray-900 border-2 border-red-500"></div>
                      <h5 className="text-red-400 font-bold text-lg mb-2">3. Cortar la Energía cuando no hay eco</h5>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Mientras sigas compartiendo intimidad emocional y charlas profundas sin dirección, el sistema no cierra.
                        No hace falta desaparecer. Pero sí bajar la dosis. Menos palabras. Más silencio interno.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* --- TAB 4: PRÁCTICA --- */}
              {wisdomTab === 'practica' && (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-8">
                     <div className="bg-gray-800/50 p-4 rounded-lg">
                        <h5 className="font-bold text-white mb-2">Regla de Oro en Vínculos</h5>
                        <ul className="text-sm text-gray-400 space-y-2 list-disc pl-4">
                          <li>No busques encuentros.</li>
                          <li>No evites encuentros.</li>
                          <li>No sostengas intimidad innecesaria.</li>
                          <li>Esto es Shiva caminando.</li>
                        </ul>
                     </div>
                     <div className="bg-gray-800/50 p-4 rounded-lg">
                        <h5 className="font-bold text-white mb-2">Check Rápido</h5>
                        <ul className="text-sm text-gray-400 space-y-2 list-disc pl-4">
                          <li>¿Estoy proponiendo o esperando?</li>
                          <li>¿Estoy presente o proyectando?</li>
                          <li>¿Hablo para conectar o para gustar?</li>
                        </ul>
                     </div>
                  </div>

                  <div>
                    <h4 className="text-orange-400 font-bold uppercase mb-4">Frases y Gestos</h4>
                    <div className="space-y-3">
                      <div className="p-3 border-l-2 border-orange-500 bg-gray-900/50">
                        <p className="text-gray-200 font-medium">"Me gustás. Me atrae compartir con vos desde otro lugar. Si no está, está todo bien."</p>
                      </div>
                      <div className="p-3 border-l-2 border-gray-600 bg-gray-900/50">
                        <p className="text-gray-400">Silencio después de proponer. No explicar. No sonreír para suavizar.</p>
                      </div>
                      <div className="p-3 border-l-2 border-gray-600 bg-gray-900/50">
                        <p className="text-gray-400">Acercarte medio paso más cuando hay tensión. Tocar una vez y soltar.</p>
                      </div>
                    </div>
                  </div>

                  <div>
                     <h4 className="text-purple-400 font-bold uppercase mb-4 mt-8">El Camino de 40 Días</h4>
                     <div className="grid grid-cols-2 gap-2 text-xs md:text-sm">
                        <div className="p-2 bg-gray-900 rounded border border-gray-800">
                          <span className="text-purple-400 font-bold">Fase 1 (1-10):</span> Cuerpo y Presencia. Habitar sin explicar.
                        </div>
                        <div className="p-2 bg-gray-900 rounded border border-gray-800">
                          <span className="text-purple-400 font-bold">Fase 2 (11-20):</span> Decisión y Corte. Decir NO.
                        </div>
                        <div className="p-2 bg-gray-900 rounded border border-gray-800">
                          <span className="text-purple-400 font-bold">Fase 3 (21-30):</span> Deseo Expresado. Sin negociar resultado.
                        </div>
                        <div className="p-2 bg-gray-900 rounded border border-gray-800">
                          <span className="text-purple-400 font-bold">Fase 4 (31-40):</span> Dirección Pura. Punto estable.
                        </div>
                     </div>
                  </div>
                </div>
              )}

              {/* --- TAB 5: VERDADES --- */}
              {wisdomTab === 'verdades' && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="p-6 bg-black border border-gray-800 rounded-lg text-center">
                    <Quote className="mx-auto text-gray-700 mb-4" />
                    <p className="text-xl md:text-2xl text-white font-serif italic">
                      "Las mujeres que realmente te van a elegir no aparecen mientras sigas disponible emocionalmente para las que no te eligen."
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                     <div className="p-4 bg-gray-900/50 border border-gray-800 rounded-lg">
                        <p className="text-gray-300 font-medium">
                          "El masculino propone. La femenina dispone. Cuando esperás señales, la polaridad se aplana y entrás en territorio fraterno."
                        </p>
                     </div>
                     <div className="p-4 bg-gray-900/50 border border-gray-800 rounded-lg">
                        <p className="text-gray-300 font-medium">
                          "No viniste a 'equilibrarte'. Viniste a liderarte."
                        </p>
                     </div>
                     <div className="p-4 bg-gray-900/50 border border-gray-800 rounded-lg">
                        <p className="text-gray-300 font-medium">
                           "Tu masculino no se activa con más emoción, se activa con ritmo, voto interno y dirección silenciosa."
                        </p>
                     </div>
                     <div className="p-4 bg-gray-900/50 border border-gray-800 rounded-lg">
                        <p className="text-gray-300 font-medium">
                           "El dolor de verla con otro es el ego masculino sintiendo: 'No fui elegido como hombre'."
                        </p>
                     </div>
                  </div>

                  <div className="p-6 bg-purple-900/20 border border-purple-500/30 rounded-lg text-center mt-8">
                     <p className="text-lg text-white font-bold uppercase tracking-widest">
                       "No persigo conexión. Me convierto en eje."
                     </p>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ConsciousWarriorTracker;